<div class="bg-background min-h-screen w-full font-sans pb-12 pt-8">
  <div class="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">

    <!-- Header Section -->
    <div class="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
      <div>
        <h1 class="text-3xl font-display font-bold text-on-background">My Profile</h1>
        <p class="text-on-surface-variant/80 text-sm mt-1">View and manage your profile details</p>
      </div>
      <button (click)="saveProfile()" [disabled]="loading"
        class="gold-button flex items-center gap-2 px-6 py-2.5 shadow-lg min-w-[120px] justify-center">
        <span *ngIf="loading" class="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
        <span *ngIf="!loading" class="material-symbols-outlined text-[20px]">save</span>
        {{ loading ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>

    <!-- Main Content Container -->
    <div
      class="bg-surface rounded-3xl shadow-sm border border-primary/10 overflow-visible flex flex-col md:flex-row min-h-[600px]">

      <!-- Vertical Tabs Sidebar (md+) / Horizontal scrolling tabs (mobile) -->
      <div
        class="w-full md:w-64 bg-surface-container-low border-b md:border-b-0 md:border-r border-primary/10 flex-shrink-0">
        <div class="flex md:flex-col overflow-x-auto md:overflow-visible p-4 gap-2 no-scrollbar">

          <button (click)="setTab('basic')"
            class="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all whitespace-nowrap text-left font-medium text-sm md:text-base"
            [ngClass]="activeTab === 'basic' ? 'bg-primary text-white shadow-md' : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary'">
            <span class="material-symbols-outlined">person</span> Basic Info
          </button>

          <button (click)="setTab('personal')"
            class="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all whitespace-nowrap text-left font-medium text-sm md:text-base"
            [ngClass]="activeTab === 'personal' ? 'bg-primary text-white shadow-md' : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary'">
            <span class="material-symbols-outlined">favorite</span> Personal Info
          </button>

          <button (click)="setTab('professional')"
            class="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all whitespace-nowrap text-left font-medium text-sm md:text-base"
            [ngClass]="activeTab === 'professional' ? 'bg-primary text-white shadow-md' : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary'">
            <span class="material-symbols-outlined">work</span> Professional Info
          </button>

          <button (click)="setTab('religion')"
            class="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all whitespace-nowrap text-left font-medium text-sm md:text-base"
            [ngClass]="activeTab === 'religion' ? 'bg-primary text-white shadow-md' : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary'">
            <span class="material-symbols-outlined">temple_hindu</span> Religion / Caste
          </button>

          <button (click)="setTab('location')"
            class="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all whitespace-nowrap text-left font-medium text-sm md:text-base"
            [ngClass]="activeTab === 'location' ? 'bg-primary text-white shadow-md' : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary'">
            <span class="material-symbols-outlined">location_on</span> Location Info
          </button>

          <button (click)="setTab('gallery')"
            class="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all whitespace-nowrap text-left font-medium text-sm md:text-base"
            [ngClass]="activeTab === 'gallery' ? 'bg-primary text-white shadow-md' : 'text-on-surface-variant hover:bg-primary/5 hover:text-primary'">
            <span class="material-symbols-outlined">photo_library</span> Photo Gallery
          </button>

        </div>
      </div>

      <!-- Content Area -->
      <div class="flex-grow p-6 md:p-8 bg-surface">
        <div *ngIf="activeTab === 'gallery'" class="mb-6 flex flex-col md:flex-row items-start md:items-center gap-4 rounded-3xl border border-primary/10 bg-surface-container-low p-4">
          <div class="h-24 w-24 overflow-hidden rounded-full border border-primary/20 bg-white shadow-sm">
            <img *ngIf="avatarPreview" [src]="avatarPreview" alt="Profile photo" class="h-full w-full object-cover" />
            <div *ngIf="!avatarPreview" class="flex h-full w-full items-center justify-center bg-surface-container">
              <span class="material-symbols-outlined text-4xl text-on-surface-variant/40">person</span>
            </div>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-on-background">Profile Photo</h3>
            <p class="text-sm text-on-surface-variant/80">Upload a clear photo so others can recognize you. If no photo is added, a default avatar will be shown.</p>
            <div class="mt-3 flex flex-wrap items-center gap-3">
              <label class="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white">
                <span class="material-symbols-outlined text-[16px]">upload</span>
                Choose Photo
                <input type="file" accept="image/*" class="hidden" (change)="onAvatarSelected($event)" />
              </label>
              <button type="button" (click)="uploadAvatar()" [disabled]="!selectedAvatarFile || loading" class="rounded-full border border-primary/20 px-4 py-2 text-sm font-semibold text-primary disabled:opacity-60">
                Upload / Update
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading && !profileData?.basic_info"
          class="flex flex-col items-center justify-center h-full text-primary">
          <span class="material-symbols-outlined animate-spin text-[48px] mb-4">progress_activity</span>
          <p class="text-sm font-medium">Loading profile details...</p>
        </div>

        <!-- Basic Info Form -->
        <form [formGroup]="basicForm" *ngIf="!loading && activeTab === 'basic'" class="animate-fade-in">
          <!-- Card 1: Identity & Demographics -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">person</span>
              <h2 class="section-title">Identity & Demographics</h2>
            </div>
            
            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">First Name <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="text" formControlName="first_name" class="form-control" placeholder="First Name">
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Last Name <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="text" formControlName="last_name" class="form-control" placeholder="Last Name">
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Gender <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openGenderDropdown()">
                    {{selectedGender || 'Select Gender'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="gender-list" *ngIf="showGenderList">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchGender($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="gender-item" *ngFor="let gender of filteredGenderList" (click)="selectGender(gender)">
                        {{gender.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Date of Birth <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <app-dob-picker formControlName="date_of_birth" customClass="form-control" [hasError]="!!(basicForm.get('date_of_birth')?.touched && basicForm.get('date_of_birth')?.invalid)"></app-dob-picker>
                <small class="text-danger d-block mt-1" *ngIf="basicForm.get('date_of_birth')?.touched && basicForm.get('date_of_birth')?.hasError('underAge')">You must be at least 18 years old.</small>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Mother Tongue <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openLanguageDropdown()">
                    {{selectedLanguageName || 'Select Mother Tongue'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showLanguageList">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchLanguage($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let lang of filteredLanguages" (click)="selectLanguage(lang)">
                        {{lang.language_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Contact Information -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">contact_mail</span>
              <h2 class="section-title">Contact Information</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Email Address <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="email" formControlName="email" class="form-control" placeholder="Email Address">
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Phone Number <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="tel" maxlength="10" inputmode="numeric" (input)="onMobileInput($event)" formControlName="mobile" class="form-control phone-input" placeholder="Phone Number">
              </div>
            </div>
          </div>
        </form>

        <!-- Personal Info Form -->
        <form [formGroup]="personalForm" *ngIf="!loading && activeTab === 'personal'" class="animate-fade-in">
          <!-- Card 1: Status & Physical Attributes -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">favorite</span>
              <h2 class="section-title">Status & Physical Attributes</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Marital Status <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('marital')">
                    {{selectedMarital || 'Select Status'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='marital'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredPersonalList" (click)="selectPersonal(item,'marital_status')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Height <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('height')">
                    {{selectedHeight ? selectedHeight + ' cm' : 'Select Height'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='height'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredHeight" (click)="selectPersonal(item,'height')">
                        {{item.name}} cm
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Weight <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('weight')">
                    {{selectedWeight ? selectedWeight + ' kg' : 'Select Weight'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='weight'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredWeight" (click)="selectPersonal(item,'weight')">
                        {{item.name}} kg
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Physical Status <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('physical')">
                    {{selectedPhysical || 'Select Physical Status'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='physical'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredPersonalList" (click)="selectPersonal(item,'physical_status')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Body Type</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('body')">
                    {{selectedBody || 'Select Body Type'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='body'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredPersonalList" (click)="selectPersonal(item,'body_type')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Lifestyle & Habits -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">restaurant_menu</span>
              <h2 class="section-title">Lifestyle & Habits</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Food Habit <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('food')">
                    {{selectedFood || 'Select Food Habit'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showPersonalDropdown==='food'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredPersonalList" (click)="selectPersonal(item,'food')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Smoking</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('smoking')">
                    {{selectedSmoking || 'Select Smoking'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='smoking'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredPersonalList" (click)="selectPersonal(item,'smoking')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Drinking</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openPersonalDropdown('drinking')">
                    {{selectedDrinking || 'Select Drinking'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list personal-list" *ngIf="showPersonalDropdown==='drinking'">
                    <input type="text" class="search-box" placeholder="Search here" (input)="searchPersonal($event)" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredPersonalList" (click)="selectPersonal(item,'drinking')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <!-- Professional Info Form -->
        <form [formGroup]="professionalForm" *ngIf="!loading && activeTab === 'professional'" class="animate-fade-in">
          <!-- Card 1: Academic Background -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">school</span>
              <h2 class="section-title">Academic Background</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Education <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openProfessionalDropdown('education')">
                    {{selectedEducation || 'Select Education'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showProfessionalDropdown==='education'">
                    <input class="search-box" placeholder="Search here" (input)="searchProfessional($event,'education')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredEducation" (click)="selectProfessional(item,'education')">
                        {{item.education_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">College / Institution <span style="font-weight: 400; font-size: 11px; color: #777;">(Optional)</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="text" formControlName="education_detail" class="form-control" placeholder="e.g., BCA at SRM Institute of Science and Technology">
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Higher Education</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openProfessionalDropdown('higher')">
                    {{selectedHigherEducation || 'Select Higher Education'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showProfessionalDropdown==='higher'">
                    <input class="search-box" placeholder="Search here" (input)="searchProfessional($event,'higher')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredHigherEducation" (click)="selectProfessional(item,'higher')">
                        {{item.highereducation_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Career & Financials -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">work</span>
              <h2 class="section-title">Career & Financials</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Employment Type <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openProfessionalDropdown('employment')">
                    {{selectedEmployment || 'Select Employment Type'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showProfessionalDropdown==='employment'">
                    <input class="search-box" placeholder="Search here" (input)="searchProfessional($event,'employment')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredEmployment" (click)="selectProfessional(item,'employment')">
                        {{item.employment_type_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Occupation Category <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openProfessionalDropdown('occupation')">
                    {{selectedOccupation || 'Select Occupation Category'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showProfessionalDropdown==='occupation'">
                    <input class="search-box" placeholder="Search here" (input)="searchProfessional($event,'occupation')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredOccupation" (click)="selectProfessional(item,'occupation')">
                        {{item.occupation_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Designation / Job Title <span style="font-weight: 400; font-size: 11px; color: #777;">(Optional)</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="text" formControlName="occupation_detail" class="form-control" placeholder="e.g., Senior Java Developer at TCS">
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Annual Income <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openProfessionalDropdown('income')">
                    {{selectedIncome || 'Select Annual Income'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showProfessionalDropdown==='income'">
                    <input class="search-box" placeholder="Search here" (input)="searchProfessional($event,'income')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredIncome" (click)="selectProfessional(item,'income')">
                        {{item.income_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Additional Income Source <span style="font-weight: 400; color: #777;">(Optional)</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openProfessionalDropdown('addIncome')">
                    {{ professionalForm.get('additional_income_source')?.value || 'Select Additional Income Source' }}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showProfessionalDropdown==='addIncome'">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let src of additionalIncomeSources" (click)="selectAddIncomeSource(src)">
                        {{ src }}
                      </div>
                      <div class="language-item" style="color: #B01E3E; font-weight: 600;" *ngIf="professionalForm.get('additional_income_source')?.value" (click)="selectAddIncomeSource('')">
                        Clear Selection
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row" *ngIf="isCustomAddIncome(professionalForm.get('additional_income_source')?.value)">
              <div class="field-label-col">
                <label class="field-label">Specify Income Source <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="text" [value]="(professionalForm.get('additional_income_source')?.value === 'Other (Specify / Type Custom)' || professionalForm.get('additional_income_source')?.value === 'Other') ? '' : professionalForm.get('additional_income_source')?.value" (input)="onCustomAddIncomeChange($event)" class="form-control" placeholder="Type custom source (e.g. Stock Dividends, Royalty, Pension)...">
              </div>
            </div>

            <div class="profile-field-row" *ngIf="professionalForm.get('additional_income_source')?.value">
              <div class="field-label-col">
                <label class="field-label">Additional Annual Income (₹) <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="number" formControlName="additional_annual_income" class="form-control" placeholder="e.g. 250000" min="1">
                <div class="error-msg" style="color: #B01E3E; font-size: 12px; margin-top: 4px;" *ngIf="professionalForm.get('additional_annual_income')?.touched && professionalForm.get('additional_annual_income')?.invalid">
                  <span *ngIf="professionalForm.get('additional_annual_income')?.errors?.['required']">Additional annual income is required when source is selected</span>
                  <span *ngIf="professionalForm.get('additional_annual_income')?.errors?.['pattern'] || professionalForm.get('additional_annual_income')?.value <= 0">Please enter a valid positive numeric amount</span>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Work Zip/Pin Code</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <input type="text" formControlName="work_zipcode" class="form-control" placeholder="Zip/Pin Code">
              </div>
            </div>
          </div>
        </form>

        <!-- Religion Info Form -->
        <form [formGroup]="religionForm" *ngIf="!loading && activeTab === 'religion'" class="animate-fade-in">
          <!-- Card 1: Religious & Astrological Details -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">temple_hindu</span>
              <h2 class="section-title">Religious & Astrological Details</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Religion <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('religion')">
                    {{selectedReligion || 'Select Religion'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='religion'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'religion')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredReligion" (click)="selectReligion(item,'religion')">
                        {{item.religion_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Non-Religious Banner -->
            <div *ngIf="isNonReligious()" class="profile-field-row mb-4" style="display: block;">
              <div style="padding: 16px; background: linear-gradient(135deg, #f0fdf4, #ecfdf5); border: 1px solid #10b981; border-radius: 12px; display: flex; align-items: center; gap: 12px;">
                <span class="material-symbols-outlined" style="color: #059669; font-size: 24px;">public</span>
                <div>
                  <h4 style="margin: 0; color: #065f46; font-size: 15px; font-weight: 700;">Secular / Non-Religious Profile</h4>
                  <p style="margin: 4px 0 0; color: #047857; font-size: 13px; line-height: 1.4;">
                    Since you selected <strong>{{ selectedReligion }}</strong>, Caste, Sub-caste, Gothram, and Dosham are hidden and marked as <strong>Not Applicable</strong>.
                  </p>
                </div>
              </div>
            </div>

            <ng-container *ngIf="!isNonReligious()">
            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Caste <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('caste')">
                    {{selectedCaste || 'Select Caste'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='caste'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'caste')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredCaste" (click)="selectReligion(item,'caste')">
                        {{item.caste_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Sub Caste</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('subcaste')">
                    {{selectedSubCaste || 'Select Sub Caste'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='subcaste'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'subcaste')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredSubCaste" (click)="selectReligion(item,'subcaste')">
                        {{item.subcaste_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Astrology Note -->
            <div class="profile-field-row mb-4" style="display: block;">
              <div style="padding: 12px 16px; background-color: #fdfaf2; border: 1px solid #f5ebd5; border-left: 4px solid #C5A059; border-radius: 12px;">
                <p style="margin: 0; font-size: 12px; color: #856404; font-style: italic; font-weight: 500; line-height: 1.5;">
                  Astrology Details (Optional) – Fill these only if they are relevant to you or your family's preferences.
                </p>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Gothram <span *ngIf="(selectedReligion || '').toLowerCase() === 'hindu'" class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('gothram')">
                    {{selectedGothram || 'Select Gothram'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='gothram'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'gothram')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredGothram" (click)="selectReligion(item,'gothram')">
                        {{item.gothram_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Dosham <span *ngIf="(selectedReligion || '').toLowerCase() === 'hindu'" class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('dosham')">
                    {{selectedDosham || 'Select Dosham'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='dosham'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'dosham')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredDosham" (click)="selectReligion(item,'dosham')">
                        {{item.name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Star / Nakshatra</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('natchr')">
                    {{ selectedNatchra || 'Select Star / Nakshatra' }}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='natchr'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'natchr')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredNatchra" (click)="selectReligion(item,'natchr')">
                        {{item.natchr_nme || item.natchr_name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Raasi / Moon Sign</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openReligionDropdown('raasi')">
                    {{ selectedRasi || 'Select Raasi / Moon Sign' }}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showReligionDropdown==='raasi'">
                    <input class="search-box" placeholder="Search here" (input)="searchReligion($event,'raasi')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredRasi" (click)="selectReligion(item,'raasi')">
                        {{item.rasi_nme || item.rasi_name}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </ng-container>
          </div>
        </form>

        <!-- Location Info Form -->
        <form [formGroup]="locationForm" *ngIf="!loading && activeTab === 'location'" class="animate-fade-in">
          <!-- Card 1: Residential Address & Region -->
          <div class="profile-section-card">
            <div class="section-header">
              <span class="material-symbols-outlined section-icon">location_on</span>
              <h2 class="section-title">Residential Address & Region</h2>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Country <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openLocationDropdown('country')">
                    {{selectedCountry || 'Select Country'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showLocationDropdown==='country'">
                    <input class="search-box" placeholder="Search here" (input)="searchLocation($event,'country')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredCountry" (click)="selectLocation(item,'country')">
                        {{item.name_0}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">State <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openLocationDropdown('state')">
                    {{selectedState || 'Select State'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showLocationDropdown==='state'">
                    <input class="search-box" placeholder="Search here" (input)="searchLocation($event,'state')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredState" (click)="selectLocation(item,'state')">
                        {{item.name_1}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">District <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openLocationDropdown('district')">
                    {{selectedDistrict || 'Select District'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showLocationDropdown==='district'">
                    <input class="search-box" placeholder="Search here" (input)="searchLocation($event,'district')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredDistrict" (click)="selectLocation(item,'district')">
                        {{item.name_2}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">City <span class="text-primary">*</span></label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <div class="custom-dropdown">
                  <div class="form-control selected-box" (click)="openLocationDropdown('city')">
                    {{selectedCity || 'Select City'}}
                    <span class="arrow material-symbols-outlined">expand_more</span>
                  </div>
                  <div class="language-list" *ngIf="showLocationDropdown==='city'">
                    <input class="search-box" placeholder="Search here" (input)="searchLocation($event,'city')" (click)="$event.stopPropagation()">
                    <div class="dropdown-item">
                      <div class="language-item" *ngFor="let item of filteredCity" (click)="selectLocation(item,'city')">
                        {{item.city_nme}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-field-row">
              <div class="field-label-col">
                <label class="field-label">Address</label>
                <span class="field-separator">:</span>
              </div>
              <div class="field-input-col">
                <textarea formControlName="address1" rows="3" class="form-control" placeholder="Full residential address"></textarea>
              </div>
            </div>
          </div>
        </form>

        <!-- Photo Gallery Management View -->
        <div *ngIf="!loading && activeTab === 'gallery'" class="animate-fade-in">
          <div class="profile-section-card">
            <div class="section-header justify-between flex-wrap gap-4">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined section-icon">photo_library</span>
                <div>
                  <h2 class="section-title mb-0">Manage Photo Gallery</h2>
                  <p class="text-xs text-on-surface-variant mt-0.5 mb-0 font-normal">Upload up to 4 additional gallery photos. Your main DP is public, but gallery photos are VIP exclusive.</p>
                </div>
              </div>
              <label class="gold-button cursor-pointer flex items-center gap-1.5 px-4 py-2 text-xs font-bold shadow-md hover:scale-105 transition-all m-0" [class.opacity-50]="galleryPhotos.length >= 4">
                <span class="material-symbols-outlined text-[16px]">add_photo_alternate</span> Upload Photos ({{galleryPhotos.length}}/4)
                <input type="file" multiple accept="image/*" (change)="onGalleryFileSelected($event)" [disabled]="galleryPhotos.length >= 4" class="hidden" />
              </label>
            </div>

            <!-- Gallery Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
              <!-- Main DP Card -->
              <div class="relative group rounded-2xl overflow-hidden border-2 border-primary bg-primary/5 aspect-[3/4] shadow-sm flex flex-col items-center justify-center text-center p-3">
                <span class="absolute top-2 left-2 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10 shadow">DP (Public)</span>
                <ng-container *ngIf="avatarPreview && avatarPreview !== defaultAvatarUrl; else dpDefault">
                  <img [src]="avatarPreview" class="w-full h-full object-cover" />
                </ng-container>
                <ng-template #dpDefault>
                  <img [src]="avatarPreview || defaultAvatarUrl" class="w-full h-full object-cover" />
                </ng-template>
                <span class="absolute bottom-3 left-3 text-xs font-bold text-primary bg-white/60 px-2 py-0.5 rounded">Main Profile DP</span>
              </div>

              <!-- Uploaded Gallery Photos -->
              <div *ngFor="let photo of galleryPhotos" class="relative group rounded-2xl overflow-hidden border border-primary/20 aspect-[3/4] bg-surface-container-low shadow-sm">
                <img *ngIf="galleryImagesBase64[photo]" [src]="'data:image/jpeg;base64,' + galleryImagesBase64[photo]" class="w-full h-full object-cover transition-transform group-hover:scale-105" />
                <div *ngIf="!galleryImagesBase64[photo]" class="w-full h-full flex items-center justify-center text-primary/40">
                  <span class="material-symbols-outlined animate-spin text-2xl">progress_activity</span>
                </div>
                <span class="absolute top-2 left-2 bg-amber-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow z-10">VIP Only</span>
                <button type="button" (click)="deleteGalleryPic(photo)" class="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full opacity-90 transition-all z-10 shadow flex items-center justify-center cursor-pointer">
                  <span class="material-symbols-outlined text-sm">delete</span>
                </button>
              </div>

              <!-- Empty Slots placeholders -->
              <label *ngFor="let i of [1,2,3,4].slice(galleryPhotos.length)" class="border-2 border-dashed border-primary/20 hover:border-primary/50 rounded-2xl aspect-[3/4] flex flex-col items-center justify-center gap-1 cursor-pointer bg-primary/5 hover:bg-primary/10 transition-all text-primary/60 hover:text-primary m-0 p-2 text-center">
                <span class="material-symbols-outlined text-3xl">add_circle</span>
                <span class="text-xs font-bold">Add VIP Pic</span>
                <input type="file" accept="image/*" (change)="onGalleryFileSelected($event)" class="hidden" />
              </label>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>